import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getPusherClient } from "@/lib/pusherClient";
import { usePusherConfig } from "@/hooks/usePusherConfig";

export interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: string;
  read: boolean;
  link: string | null;
  created_at: string;
  category: string;
  severity: string;
  actor_user_id: string | null;
  entity_type: string | null;
  entity_id: string | null;
  is_seen: boolean;
  read_at: string | null;
  metadata_json: any;
  icon: string | null;
}

export function useNotifications() {
  const { user } = useAuth();
  const { key, cluster, enabled: pusherEnabled } = usePusherConfig();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);
    if (data) {
      setNotifications(data as unknown as Notification[]);
      setUnreadCount(data.filter((n: any) => !n.read).length);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // 30-second polling fallback to catch any missed Realtime events
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  // Realtime data refresh — Pusher or Supabase Realtime
  useEffect(() => {
    if (!user) return;

    // ── Pusher path ──
    if (pusherEnabled && key && cluster) {
      const pusher = getPusherClient(key, cluster);
      if (!pusher) return;

      const channel = pusher.subscribe(`private-user.${user.id}`);
      channel.bind("notification", () => fetchNotifications());
      channel.bind("notification-update", () => fetchNotifications());

      return () => {
        channel.unbind("notification");
        channel.unbind("notification-update");
        // Don't unsubscribe — the listener hook manages the subscription
      };
    }

    // ── Supabase Realtime fallback ──
    const channel = supabase
      .channel(`notifications-data-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => fetchNotifications()
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        () => fetchNotifications()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, pusherEnabled, key, cluster, fetchNotifications]);

  const markAsRead = async (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true, read_at: new Date().toISOString() } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    await supabase.from("notifications").update({ read: true, read_at: new Date().toISOString() } as any).eq("id", id);
  };

  const markAsUnread = async (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: false, read_at: null } : n))
    );
    setUnreadCount((c) => c + 1);
    await supabase.from("notifications").update({ read: false, read_at: null } as any).eq("id", id);
  };

  const markAllAsRead = async () => {
    if (!user) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true, read_at: new Date().toISOString() })));
    setUnreadCount(0);
    await supabase
      .from("notifications")
      .update({ read: true, read_at: new Date().toISOString() } as any)
      .eq("user_id", user.id)
      .eq("read", false);
  };

  const markAsSeen = async (ids: string[]) => {
    if (!ids.length) return;
    setNotifications((prev) =>
      prev.map((n) => (ids.includes(n.id) ? { ...n, is_seen: true } : n))
    );
    await supabase
      .from("notifications")
      .update({ is_seen: true } as any)
      .in("id", ids);
  };

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    markAsSeen,
    refetch: fetchNotifications,
  };
}
